import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { AlertCircle, Loader2 } from 'lucide-react';

export const AdminLogin = () => {
  const navigate = useNavigate();
  const { loginWithGoogle, loginWithPassword, error } = useAuth();
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [localError, setLocalError] = useState(null);

  const handleGoogleLogin = () => {
    loginWithGoogle();
  };

  const handlePasswordLogin = async (e) => {
    e.preventDefault();
    setLocalError(null);
    setIsLoading(true);
    
    try {
      await loginWithPassword(email, password);
      navigate('/admin');
    } catch (err) {
      setLocalError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F9F9F7] flex items-center justify-center p-4" data-testid="admin-login">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <img 
            src="https://customer-assets.emergentagent.com/job_aravell-staging/artifacts/ibb3s625_UGOLF%20ARAVELL%20-%20ANDORRA%20-%20BLANCO.svg"
            alt="UGOLF Aravell"
            className="h-16 mx-auto brightness-0"
          />
          <h1 className="mt-4 text-2xl font-display font-semibold text-[#1A4D2E]">
            Administració
          </h1>
          <p className="text-sm text-[#666666] mt-2">
            Accedeix al panell de gestió
          </p>
        </div>

        {/* Login Card */}
        <div className="bg-white border border-[#E5E5E5] p-8 shadow-sm">
          {(error || localError) && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 text-sm flex items-start gap-3">
              <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
              <span>{error || localError}</span>
            </div>
          )}

          {/* Google Login - Primary */}
          <Button
            onClick={handleGoogleLogin}
            className="w-full h-12 bg-white border border-[#E5E5E5] text-[#333333] hover:bg-[#F5F5F5] flex items-center justify-center gap-3"
            data-testid="google-login-btn"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Continuar amb Google
          </Button>

          <div className="my-6 flex items-center">
            <div className="flex-1 border-t border-[#E5E5E5]"></div>
            <span className="px-4 text-sm text-[#888888]">o</span>
            <div className="flex-1 border-t border-[#E5E5E5]"></div>
          </div>

          {/* Password Login - Secondary */}
          {!showPasswordForm ? (
            <Button
              variant="outline"
              onClick={() => setShowPasswordForm(true)}
              className="w-full h-12"
              data-testid="show-password-form-btn"
            >
              Accedir amb contrasenya
            </Button>
          ) : (
            <form onSubmit={handlePasswordLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="email@exemple.com"
                  required
                  data-testid="email-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Contrasenya</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="********"
                  required
                  data-testid="password-input"
                />
              </div>
              <Button
                type="submit"
                className="w-full h-12 bg-[#1A4D2E] hover:bg-[#153d25] text-white"
                disabled={isLoading}
                data-testid="password-login-btn"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Accedint...
                  </>
                ) : (
                  'Accedir'
                )}
              </Button>
              <button
                type="button"
                onClick={() => setShowPasswordForm(false)}
                className="w-full text-sm text-[#666666] hover:text-[#1A4D2E]"
              >
                Tornar enrere
              </button>
            </form>
          )}
        </div>

        {/* Back to site */}
        <div className="text-center mt-6">
          <a 
            href="/"
            className="text-sm text-[#666666] hover:text-[#1A4D2E] transition-colors"
          >
            ← Tornar al lloc web
          </a>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
